import { useAtom } from 'jotai';
import { lazy, Suspense, useEffect, type FunctionComponent } from 'react';
import { Route, Routes } from 'react-router-dom';
import { sridsWithUserReloadAtom } from './atoms';
import FullPageLoader from './components/FullPageLoader';
import { ProjectErrorBoundary } from './components/ProjectErrorBoundary';
import { getOrgSlugFromHostname } from './core/domain';
import Layout from './layout/Layout';

// Every page is its own chunk, so the login page and dashboard don't pay for
// the project pages (three.js, the point-cloud loader, IFC fragments). Layout
// and ProjectWrapper wrap their <Outlet> in Suspense so the sidebar stays put
// while a page chunk loads; this top-level boundary covers the rest.
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ActivatePage = lazy(() => import('./pages/auth/ActivatePage'));
const CallbackPage = lazy(() => import('./pages/auth/CallbackPage'));
const EmailVerificationPage = lazy(() => import('./pages/auth/EmailVerificationPage'));
const EmailVerifiedPage = lazy(() => import('./pages/auth/EmailVerifiedPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const HandoffPage = lazy(() => import('./pages/auth/HandoffPage'));
const InvitationPage = lazy(() => import('./pages/auth/InvitationPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const LogoutPage = lazy(() => import('./pages/auth/LogoutPage'));
const OnboardingInvitePage = lazy(() => import('./pages/auth/OnboardingInvitePage'));
const OnboardingOrgPage = lazy(() => import('./pages/auth/OnboardingOrgPage'));
const OnboardingSurveyPage = lazy(() => import('./pages/auth/OnboardingSurveyPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const VerifySignupPage = lazy(() => import('./pages/auth/VerifySignupPage'));
const ArchivedProjectsPage = lazy(() => import('./pages/ArchivedProjects'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'));
const OrganizationsLoaderPage = lazy(() => import('./pages/OrganizationsLoader'));
const OrganizationUsers = lazy(() => import('./pages/OrganizationUsers'));
const AttributeTable = lazy(() =>
  import('./pages/Project/AttributeTable').then((m) => ({ default: m.AttributeTable })),
);
const AnalysesPage = lazy(() =>
  import('./pages/Project/Analyses/AnalysesPage').then((m) => ({ default: m.AnalysesPage })),
);
const ProjectEditPage = lazy(() => import('./pages/Project/ProjectEdit'));
const LegacyLayersRedirect = lazy(() => import('./pages/Project/LegacyLayersRedirect'));
const LegacyUploadRedirect = lazy(() => import('./pages/Project/LegacyUploadRedirect'));
const LegacyAnalysesRedirect = lazy(() => import('./pages/Project/LegacyAnalysesRedirect'));
const ProjectUploadPage = lazy(() => import('./pages/Project/ProjectUpload'));
const ProjectWrapper = lazy(() => import('./pages/Project/ProjectWrapper'));
const ReferralsPage = lazy(() => import('./pages/ReferralsPage'));
const SharedPage = lazy(() => import('./pages/SharedPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));

interface iRouterProps {}

const Router: FunctionComponent<iRouterProps> = () => {
  const [srids, loadSrids] = useAtom(sridsWithUserReloadAtom);
  const isOrgDomain = !!getOrgSlugFromHostname();

  useEffect(() => {
    // Load srids when the app starts
    loadSrids();
  }, [srids.user?.id, loadSrids]);
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route path='/auth/login' element={<LoginPage />} />
        <Route path='/auth/handoff' element={<HandoffPage />} />
        <Route path='/auth/register' element={<RegisterPage />} />
        <Route path='/auth/invitation' element={<InvitationPage />} />
        <Route path='/auth/email' element={<EmailVerificationPage />} />
        <Route path='/auth/email/verified' element={<EmailVerifiedPage />} />
        <Route path='/auth/activate' element={<ActivatePage />} />
        <Route path='/auth/forgot' element={<ForgotPasswordPage />} />
        <Route path='/auth/reset' element={<ResetPasswordPage />} />
        <Route path='/auth/logout' element={<LogoutPage />} />
        <Route path='/auth/signup' element={<SignupPage />} />
        <Route path='/auth/verify' element={<VerifySignupPage />} />
        <Route path='/auth/onboarding/org' element={<OnboardingOrgPage />} />
        <Route path='/auth/onboarding/invite' element={<OnboardingInvitePage />} />
        <Route path='/auth/onboarding/survey' element={<OnboardingSurveyPage />} />
        <Route path='/auth/callback' element={<CallbackPage />} />
        <Route path='/shared/:projectId' element={<SharedPage />} />
        {isOrgDomain ? (
          <Route path='/' element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path='account' element={<AccountPage />} />
            <Route path='users' element={<OrganizationUsers />} />
            <Route path='settings' element={<OrganizationSettings />} />
            <Route path='subscription' element={<SubscriptionPage />} />
            <Route path='referrals' element={<ReferralsPage />} />
            <Route path='archived-projects' element={<ArchivedProjectsPage />} />
            <Route
              path='projects/:projectId'
              element={
                <ProjectErrorBoundary>
                  <ProjectWrapper />
                </ProjectErrorBoundary>
              }
            >
              <Route index element={<AttributeTable />} />
              <Route path='datacenter' element={<ProjectUploadPage />} />
              <Route path='datacenter/analyses' element={<AnalysesPage />} />
              <Route path='datacenter/batch/:batchId' element={<LegacyUploadRedirect />} />
              <Route path='upload' element={<LegacyUploadRedirect />} />
              <Route path='upload/batch/:batchId' element={<LegacyUploadRedirect />} />
              <Route path='edit' element={<ProjectEditPage />} />
              <Route path='layers' element={<LegacyLayersRedirect />} />
              <Route path='layers/:layerId/attributes' element={<AttributeTable />} />
              <Route path='analyses' element={<LegacyAnalysesRedirect />} />
            </Route>
          </Route>
        ) : (
          <Route index element={<OrganizationsLoaderPage />} />
        )}
        <Route path='*' element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
